import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AdminAPI,
  useImportDemoSpaces,
  type DemoSpacesImportResult,
} from "@/features/admin";
import { downloadBlob } from "@/features/utils/blob";
import { Download, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Load an export and save it, turning a failure into a toast. Shared by the three
 * downloads of the page; the file itself is the server's.
 */
export const saveFile = async (load: () => Promise<Blob>, filename: string) => {
  try {
    downloadBlob(await load(), filename);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Export impossible");
  }
};

/**
 * The file's text. Excel in French saves a plain "CSV" as Windows-1252, which read as
 * UTF-8 turns every accent of a salon's name and address into U+FFFD: UTF-8 is tried
 * strictly, and the legacy encoding is the fallback.
 */
const readText = async (file: File): Promise<string> => {
  const bytes = await file.arrayBuffer();
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
};

export const ImportCard = () => {
  const input = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<DemoSpacesImportResult | null>(null);
  const importCsv = useImportDemoSpaces();

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const outcome = await importCsv.mutateAsync(await readText(file));
      setResult(outcome);
      toast.success(
        `${outcome.queued} espace(s) en cours de création` +
          (outcome.skipped.length
            ? `, ${outcome.skipped.length} ignoré(s)`
            : ""),
      );
    } catch (error) {
      setResult(null);
      toast.error(error instanceof Error ? error.message : "Import impossible");
    } finally {
      if (input.current) input.current.value = "";
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <p className="text-sm text-muted-foreground">
          Un fichier CSV avec une ligne par entreprise (tous métiers) : le site
          web, ou à défaut le nom de l'entreprise, est obligatoire ; le reste
          (secteur, prénom, nom, adresse, code postal, ville, notes) sert au
          courrier. Le secteur permet de comparer les lots entre eux, et range
          l'entreprise sous l'un des sites (colonne niche : beauty, drive,
          healthcare ou realestate, sinon lue dans le secteur) dont elle reçoit
          le flyer. Chaque
          entreprise obtient son espace, configuré depuis son site (sans site :
          depuis le nom, le secteur, la ville et les notes), et un code à 6
          chiffres. Ce qui manque est inventé et signalé « fictif » : des
          prestations avec durée et prix, une équipe de trois prénoms.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() =>
              saveFile(
                () => AdminAPI.getDemoSpacesTemplateCsv(),
                "espaces-demo-modele.csv",
              )
            }
          >
            <Download className="h-4 w-4" />
            Télécharger le modèle
          </Button>
          <Button
            disabled={importCsv.isPending}
            onClick={() => input.current?.click()}
          >
            <Upload className="h-4 w-4" />
            {importCsv.isPending ? "Envoi…" : "Importer un CSV"}
          </Button>
          <input
            ref={input}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(event) => onFile(event.target.files?.[0])}
          />
        </div>

        {result && result.skipped.length > 0 ? (
          <div className="rounded-md border p-3 text-sm">
            <div className="mb-1 font-medium">Lignes ignorées</div>
            <ul className="max-h-40 space-y-0.5 overflow-auto text-muted-foreground">
              {result.skipped.map((line) => (
                <li key={`${line.line}-${line.website}`}>
                  Ligne {line.line}
                  {line.website ? ` (${line.website})` : ""} : {line.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
